/**
 * Service Analytics - Couche métier pour l'analyse des performances (TimescaleDB)
 * Délègue les appels HTTP au client API et ajoute de la logique métier
 */

import { analyticsClient } from '../api'
import { TopActivitiesResponseDto } from '@sphere/analytics/domain'
import type {
  UserSubjectMetrics,
  RecentAttempt,
  UserSummary,
  UserTeachingTimeline,
  UserLearningDifficulties,
  LearningActivityEvent,
  EstablishmentSummary,
  ClassMetrics,
  GradeMetrics,
  TopActivity,
  DailyActivity,
} from '@sphere/analytics/domain'

// ============================================================================
// Ingestion d'événements
// ============================================================================

export async function ingestLearningActivity(
  event: LearningActivityEvent
): Promise<{ event_id: string; status: string }> {
  const response = await analyticsClient.ingest.learningActivity(event)
  return response.data
}

// ============================================================================
// Métriques utilisateur
// ============================================================================

export async function getUserSubjectDailyMetrics(
  userId: string,
  subjectId: string,
  params?: {
    date_from?: string
    date_to?: string
    limit?: number
  }
): Promise<UserSubjectMetrics> {
  const response = await analyticsClient.metrics.getUserSubjectDaily(
    userId,
    subjectId,
    params
  )
  return response.data
}

export async function getRecentAttempts(
  userId: string,
  params?: {
    since_days?: number
    limit?: number
  }
): Promise<{
  user_id: string
  since_days: number
  attempts: RecentAttempt[]
}> {
  const response = await analyticsClient.metrics.getRecentAttempts(
    userId,
    params
  )
  return response.data
}

export async function getUserProgressSummary(
  userId: string,
  params?: {
    since_days?: number
  }
): Promise<UserSummary> {
  const response = await analyticsClient.metrics.getUserSummary(userId, params)
  return response.data
}

export async function getUserTeachingTimeline(
  userId: string,
  teachingId: string,
  params?: {
    since_days?: number
    granularity?: 'day' | 'week'
    events_limit?: number
  }
): Promise<UserTeachingTimeline> {
  const response = await analyticsClient.metrics.getUserTeachingTimeline(
    userId,
    teachingId,
    params
  )
  return response.data
}

export async function getUserLearningDifficulties(
  userId: string,
  params?: {
    since_days?: number
    teaching_id?: string
    subject_id?: string
    limit?: number
  }
): Promise<UserLearningDifficulties> {
  const response = await analyticsClient.recommendations.getUserDifficulties(
    userId,
    params
  )
  return response.data
}

// ============================================================================
// Health check
// ============================================================================

export async function checkAnalyticsHealth(): Promise<{
  status: string
  service: string
  version: string
  database: string
}> {
  const response = await analyticsClient.health()
  return response.data
}

// ============================================================================
// Establishment metrics
// ============================================================================

export async function getEstablishmentSummary(
  tenantId: string,
  params?: {
    since_days?: number
  }
): Promise<EstablishmentSummary> {
  const response = await analyticsClient.metrics.getEstablishmentSummary(
    tenantId,
    params
  )
  return response.data
}

export async function getEstablishmentByClass(
  tenantId: string,
  params?: {
    since_days?: number
  }
): Promise<{
  tenant_id: string
  since_days: number
  classes: ClassMetrics[]
}> {
  const response = await analyticsClient.metrics.getEstablishmentByClass(
    tenantId,
    params
  )
  return response.data
}

export async function getEstablishmentByGrade(
  tenantId: string,
  params?: {
    since_days?: number
  }
): Promise<{
  tenant_id: string
  since_days: number
  grades: GradeMetrics[]
}> {
  const response = await analyticsClient.metrics.getEstablishmentByGrade(
    tenantId,
    params
  )
  return response.data
}

export async function getEstablishmentTopActivities(
  tenantId: string,
  params?: {
    since_days?: number
    limit?: number
  }
): Promise<TopActivitiesResponseDto> {
  const response = await analyticsClient.metrics.getEstablishmentTopActivities(
    tenantId,
    params
  )
  return response.data
}

export async function getEstablishmentDailyActivity(
  tenantId: string,
  params?: {
    since_days?: number
  }
): Promise<{
  tenant_id: string
  since_days: number
  daily_metrics: DailyActivity[]
}> {
  const response = await analyticsClient.metrics.getEstablishmentDailyActivity(
    tenantId,
    params
  )
  return response.data
}
